import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import MagazineCard from '@shared/components/common/MagazineCard';
import Pagination from '@shared/components/pagination/Pagination';
import FeaturedCarousel from '@user/components/publication/FeaturedCarousel';
import { useStore } from '@shared/hooks/useStore';
import { showQuickViewModal } from '@user/components/publication/QuickViewModal';
import { ROUTES } from '@shared/configs/constants';
import { Magazine } from '@shared/types/magazine';

const MagazinesPage = observer(() => {
    const navigate = useNavigate();
    const { magazineStore, publicationStore, apiStore, modalStore } = useStore();
    const { magazines, totalMagazines } = magazineStore;
    const { newPublications: featuredPublications } = publicationStore;
    const [currentPage, setCurrentPage] = useState(1);
    const isLoading = apiStore.isLoading;
    const pageSize = 24;
    const fetchMagazines = async (page: number) => {
        magazineStore.fetchMagazines({
            page: page,
            size: pageSize
        });
    };

    const fetchFeatured = async () => {
        publicationStore.fetchNewPublications({ size: 10 });
    };

    useEffect(() => {
        fetchFeatured();
    }, []);

    useEffect(() => {
        fetchMagazines(currentPage);
    }, [currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleQuickView = (magazine: Magazine) => {
        showQuickViewModal(magazine as any, modalStore);
    };

    const handleMagazineClick = (magazine: Magazine) => {
        navigate(ROUTES.magazineDetail.href.replace(':id', magazine.id.toString()));
    };

    return (
        <>
            {/* Featured Carousel Section */}
            {featuredPublications.length > 0 && (
                <FeaturedCarousel items={featuredPublications} />
            )}

            <section className='container mx-auto px-4 py-6 md:py-12'>
                {isLoading && magazines.length === 0 ?
                    (
                        <></>
                    ) :
                    (
                        <>
                            <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-[24px] lg:gap-[36px] xl:gap-[48px]'>
                                {magazines.map(magazine => (
                                    <MagazineCard
                                        key={magazine.id}
                                        magazine={magazine}
                                        onQuickView={handleQuickView}
                                        onClick={handleMagazineClick}
                                    />
                                ))}
                            </div>

                            <div className='mt-16'>
                                <Pagination
                                    type='full-pagination'
                                    currentPage={currentPage}
                                    totalRecords={totalMagazines}
                                    pageSize={pageSize}
                                    onPageChange={handlePageChange}
                                />
                            </div>

                            {magazines.length === 0 && !isLoading && (
                                <div className='text-center py-20 text-gray-500'>
                                    雑誌が見つかりませんでした。
                                </div>
                            )}
                        </>
                    )}
            </section>
        </>
    );
});

export default MagazinesPage;
